// @flow

/**
 * FetchMoreScrollTracker watches to see if the user has scrolled to the bottom
 * and calls the provided fetchMore function if so.
 *
 * Note: FetchMoreScrollTracker cares about the bottom of the content, not the
 * bottom of the scroll container. For 99% of cases, these are the same, but
 * it's possible that the content extends beyond the scroll container.
 *
 * Note: FetchMoreScrollTracker starts measuring scroll distance from the top of
 * the content, not from the top of the scroll container. In cases where the
 * content is offset from the top of the scroll container (say, a page has a
 * header and title above the grid), this content will be ignored in calculating
 * the fetch cutoff point.
 */

import throttle from 'lodash/throttle';
import { ScrollTracker } from 'app/common/react/components/Masonry9/ScrollTracker';
import Layout, { type Size, type Position } from 'app/common/react/components/Masonry9/Layout';

// FetchMoreScrollTracker will fetch new content when the distance between the
// bottom of the page and the bottom of the content falls below a threshold.
export type getFetchOverscanProps = {|
  scrollPosition: Position,
  containerSize: Size,
  layoutSize: Size,
|};

// By default, FetchMoreScrollTracker will call to fetch when the viewport
// bottom is within two viewport heights from content bottom.
const DEFAULT_FETCH_MORE_THRESHOLD_FACTOR = 2;
const defaultFetchOverscan = ({ containerSize }: getFetchOverscanProps) =>
  containerSize.height * DEFAULT_FETCH_MORE_THRESHOLD_FACTOR;

const THROTTLE_DELAY = 100; // for parity with Masonry 8 https://github.com/pinterest/gestalt/blob/v0.104.0/packages/gestalt/src/ScrollFetch.js#L31

export default class FetchMoreScrollTracker<ItemType> implements ScrollTracker<ItemType> {
  fetchMore: () => Promise<boolean>;

  scrollPosition: ?Position;

  containerSize: ?Size;

  layoutSize: ?Size;

  isMeasuring: boolean = false;

  isFetching: boolean = false;

  items: ?Array<ItemType>;

  fetchOverscan: number | ((props: getFetchOverscanProps) => number);

  constructor(
    fetchMore: () => Promise<boolean>,
    fetchOverscan?: number | ((props: getFetchOverscanProps) => number) = defaultFetchOverscan,
  ) {
    this.fetchMore = fetchMore;
    this.fetchOverscan = fetchOverscan;
  }

  // Whenever the layout is resized, ScrollTracker needs to be notified.
  handleLayoutUpdated(layout: Layout<ItemType>) {
    // If the number of items grows, we can clear the "isFetching" flag.
    if (!this.items || (this.items && this.items.length !== layout.items.length)) {
      this.isFetching = false;
    }

    // Save layout info.
    this.layoutSize = layout.size;
    this.isMeasuring = !!layout.isMeasuring;
    this.items = layout.items;

    this.fetchMoreIfReady();
  }

  // Whenever the scroll container is resize, ScrollTracker needs to be
  // notified.
  handleContainerResized(size: Size) {
    this.containerSize = size;

    this.fetchMoreIfReady();
  }

  // Whenever the scroll container is scrolled, ScrollTracker needs to be
  // notified.
  handleContainerScrolled(scrollPosition: Position) {
    this.scrollPosition = scrollPosition;

    this.fetchMoreIfReady();
  }

  fetchMoreIfReady = throttle(() => {
    if (this.isMeasuring || this.isFetching) {
      return;
    }

    if (!this.scrollPosition || !this.containerSize || !this.layoutSize) {
      return;
    }

    const visibleContentBottom = this.scrollPosition.top + this.containerSize.height;

    const fetchOverscanVal =
      typeof this.fetchOverscan === 'function'
        ? this.fetchOverscan({
            layoutSize: this.layoutSize,
            scrollPosition: this.scrollPosition,
            containerSize: this.containerSize,
          })
        : this.fetchOverscan;

    if (visibleContentBottom + fetchOverscanVal < this.layoutSize.height) {
      return;
    }

    this.isFetching = true;

    this.fetchMore();
  }, THROTTLE_DELAY);
}
